/**
 * AutoPilot BaseFareOverride (BFO) helpers (RM-1201).
 * BFO behavior activates only from Platform Purchase's canonical pricing context inside
 * CDS's supported seats-on-results and standard modern-lock checkout journeys; every
 * helper here is pure and never mutates the pricing or seat objects it receives.
 */

/** Typed Platform Purchase error prefix preserved on ticket-poll and payment mismatches. */
export const BFO_PRICE_REFRESH_REQUIRED = 'base_fare_override_price_refresh_required';

/**
 * Platform Purchase `pricing_variant` values accepted on result trip-details requests.
 * Absent preserves legacy (one-way) behavior; the selector is inert unless the
 * server-owned leg association is an active BFO.
 */
export const BFO_PRICING_VARIANTS = Object.freeze({
  departure: 'round_trip_departure',
  return: 'round_trip_return',
});

/**
 * Checks a pricing context for the exact canonical AutoPilot BFO discriminators.
 * @param {Object} pricingContext - Camelized pricing context from Platform Purchase.
 * @returns {boolean} True only when every canonical discriminator matches exactly.
 */
export function isBfoPricingContext(pricingContext) {
  return Boolean(
    pricingContext &&
      pricingContext.pricingKind === 'base_fare_override' &&
      pricingContext.baseFareSource === 'autopilot' &&
      pricingContext.displayAsDiscount === false,
  );
}

/**
 * Checks whether a pricing object carries the canonical BFO context.
 * @param {Object} pricing - Applicable leg pricing object.
 * @returns {boolean} True when the pricing context is canonical BFO.
 */
export function isBfoPricing(pricing) {
  return isBfoPricingContext(pricing?.pricingContext);
}

/**
 * Checks the flow facts available at a seat-selector instance. This shared boundary
 * keeps result/purchase seat presentation out of exchange, open-ticket, connection,
 * Quick Purchase, bulk, and legacy-lock variants even when additive BFO fields exist.
 * Tenant and canonical pricing checks remain the caller's responsibility because the
 * result selector does not yet have a purchase.
 * @param {Object} params - Seat-selection flow facts.
 * @param {Object} params.features - Whitelabel features.
 * @param {boolean} params.isExchange - Whether this is an exchange selector.
 * @param {boolean} params.isOpenTicket - Whether the selected leg is open ticket.
 * @param {boolean} params.hasConnections - Whether the selected leg has connections.
 * @param {boolean} params.isQuickPurchase - Whether this is a Quick Purchase selector.
 * @param {boolean} params.isBulkTicket - Whether this is a bulk-ticket selector.
 * @returns {boolean} True only for the supported standard/result seat flow.
 */
export function isBfoSupportedSeatSelectionFlow({
  features,
  isExchange = false,
  isOpenTicket = false,
  hasConnections = false,
  isQuickPurchase = false,
  isBulkTicket = false,
} = {}) {
  return !(
    features?.USE_LEGACY_LOCK_TICKETS ||
    isExchange ||
    isOpenTicket ||
    hasConnections ||
    isQuickPurchase ||
    isBulkTicket
  );
}

/**
 * Derives the Platform Purchase `pricing_variant` for a pre-purchase (results) seat map.
 * Only a canonical BFO round-trip result leg in a supported seat-selection flow yields a
 * value; one-way, ordinary pricing, and excluded flows resolve to `undefined` so the
 * legacy request is preserved byte-for-byte. Purchase-scoped requests never use this.
 * @param {Object} params - Result seat-selector facts.
 * @param {Object} params.pricing - Applicable result pricing for the leg.
 * @param {string} params.way - Result leg ('departure' or 'return').
 * @param {boolean} params.isRoundTrip - Explicit round-trip fact from the result card.
 * @param {Object} params.features - Whitelabel features.
 * @param {string} params.brand - Current whitelabel brand.
 * @param {boolean} params.isExchange - Whether this is an exchange selector.
 * @param {boolean} params.isOpenTicket - Whether the trip is open ticket.
 * @param {boolean} params.hasConnections - Whether the trip has connections.
 * @returns {string|undefined} The backend enum, or `undefined` to omit the selector.
 */
export function getResultPricingVariant({
  pricing,
  way,
  isRoundTrip = false,
  features,
  brand,
  isExchange = false,
  isOpenTicket = false,
  hasConnections = false,
} = {}) {
  if (brand !== 'cds' || !isRoundTrip || !isBfoPricing(pricing)) return undefined;
  if (!isBfoSupportedSeatSelectionFlow({ features, isExchange, isOpenTicket, hasConnections })) {
    return undefined;
  }
  return way === 'return' ? BFO_PRICING_VARIANTS.return : BFO_PRICING_VARIANTS.departure;
}

/**
 * Checks whether the purchase journey is one where BFO behavior may activate:
 * CDS's seats-on-results and standard modern-lock checkout for one-way and round
 * trips. Excluded flows (exchange, Quick Purchase, bulk tickets, connections and
 * legacy ticket locking) stay on their current behavior even when additive BFO
 * fields are present.
 * @param {Object} params - Helper params.
 * @param {Object} params.purchase - Plain purchase state.
 * @param {Object} params.features - Whitelabel features.
 * @param {string} params.brand - Current whitelabel brand.
 * @returns {boolean} True when the journey supports BFO behavior.
 */
export function isBfoSupportedJourney({ purchase, features, brand } = {}) {
  if (!purchase || brand !== 'cds') return false;

  const hasConnections = (way) => Boolean(way?.hasConnections) || (way?.fragments?.length ?? 0) > 1;

  const isOpenTicket = (way) => {
    const fragment = way?.fragments?.[0];
    return Boolean(way?.openTicket || fragment?.openTicket || fragment?.trip?.openTicket);
  };

  return isBfoSupportedSeatSelectionFlow({
    features,
    isExchange: purchase.isExchange,
    isOpenTicket:
      purchase.openTicket || isOpenTicket(purchase.departs) || isOpenTicket(purchase.returns),
    hasConnections: hasConnections(purchase.departs) || hasConnections(purchase.returns),
    isQuickPurchase: purchase.quickPurchase,
    isBulkTicket: purchase.bulkTicket,
  });
}

/**
 * Per-leg BFO context detection across the provisional/purchase boundary. A leg is
 * treated as BFO when either side carries the canonical context, so an authoritative
 * fetch can still run when the context changed at the purchase boundary.
 * @param {Object} params - Helper params.
 * @param {Object} params.provisionalPricing - Result-level pricing for the leg.
 * @param {Object} params.purchasePricing - Returned purchase pricing for the leg.
 * @returns {boolean} True when either side is canonical BFO.
 */
export function hasBfoLegContext({ provisionalPricing, purchasePricing } = {}) {
  return isBfoPricing(provisionalPricing) || isBfoPricing(purchasePricing);
}

/**
 * Picks the applicable pricing object for a leg following the existing one-way /
 * departure-round-trip / return-round-trip selection.
 * @param {Object} fragmentOrTrip - Purchase fragment or trip carrying pricing keys.
 * @param {Object} params - Helper params.
 * @param {string} params.way - Purchase leg ('departs' or 'returns').
 * @param {boolean} params.isRoundTrip - Whether the purchase is a round trip.
 * @returns {Object|undefined} The applicable pricing object.
 */
export function getApplicableLegPricing(fragmentOrTrip, { way, isRoundTrip } = {}) {
  if (!fragmentOrTrip) return undefined;
  if (!isRoundTrip) return fragmentOrTrip.pricing;

  return way === 'returns' || way === 'return'
    ? fragmentOrTrip.roundTripPricing
    : fragmentOrTrip.departureRoundTripPricing;
}

/**
 * Resolves the effective selected-seat fare for display and comparison. On a canonical
 * BFO leg a present dynamic quote (nullish check, so numeric strings and zero survive)
 * is the effective fare; otherwise the provider seat price applies. The input seat is
 * never mutated and its raw metadata is preserved by the caller.
 * @param {Object} params - Helper params.
 * @param {Object} params.pricing - Applicable leg pricing object.
 * @param {Object} params.seat - Seat object (raw provider metadata intact).
 * @returns {number|string|undefined} Effective fare value for the seat.
 */
export function resolveBfoEffectiveSeatPrice({ pricing, seat } = {}) {
  if (!seat) return undefined;
  if (isBfoPricing(pricing) && seat.dynamicPrice != null) return seat.dynamicPrice;
  return seat.price;
}

/**
 * Canonical seat floor as a string; a missing single-floor value is floor "1".
 * @param {Object} seat - Seat object.
 * @returns {string} Normalized floor value.
 */
const normalizeSeatFloor = (seat) => String(seat?.seatFloor ?? seat?.seatLevel ?? '1');

/**
 * Canonical seat identity (number + normalized floor) as a comparable string key.
 * @param {Object} seat - Seat object.
 * @returns {string} Identity key.
 */
const seatIdentityKey = (seat) =>
  `${String(seat?.number ?? '').trim()}|${normalizeSeatFloor(seat)}`;

/**
 * Reconciles provisional BFO selections against the authoritative purchase-scoped bus
 * map. Every provisional seat must resolve unambiguously to an available authoritative
 * seat with the same normalized identity, BFO eligibility, and numerically equal
 * effective quote, and the canonical context must not appear, disappear, or lose its
 * canonical classification across the provisional/purchase/details boundary.
 * @param {Object} params - Helper params.
 * @param {Array} params.selectedSeats - Provisional selected seats for the leg.
 * @param {Object} params.provisionalPricing - Result-level applicable pricing.
 * @param {Object} params.purchasePricing - Returned purchase applicable pricing.
 * @param {Object} params.authoritativePricing - Purchase-scoped trip-details pricing.
 * @param {Object|Array} params.authoritativeSeats - Authoritative seats keyed map or list.
 * @returns {{ok: true, seats: Array}|{ok: false, reason: string}} Reconciliation outcome
 *   with the authoritative seat objects on success.
 */
export function reconcileBfoSelectedSeats({
  selectedSeats = [],
  provisionalPricing,
  purchasePricing,
  authoritativePricing,
  authoritativeSeats,
} = {}) {
  const classifications = [provisionalPricing, purchasePricing, authoritativePricing].map(
    isBfoPricing,
  );
  if (classifications.some((isBfo) => isBfo !== classifications[0])) {
    return { ok: false, reason: 'context_mismatch' };
  }

  const seatsList = Array.isArray(authoritativeSeats)
    ? authoritativeSeats
    : Object.entries(authoritativeSeats || {}).reduce((seats, [key, seat]) => {
        if (key !== 'categories') seats.push(seat);
        return seats;
      }, []);

  const matchedSeats = [];
  for (let index = 0; index < selectedSeats.length; index += 1) {
    const seat = selectedSeats[index];
    const identity = seatIdentityKey(seat);
    const candidates = seatsList.filter(
      (authoritative) => seatIdentityKey(authoritative) === identity,
    );

    if (candidates.length === 0) return { ok: false, reason: 'seat_missing' };
    if (candidates.length > 1) return { ok: false, reason: 'seat_ambiguous' };

    const [authoritative] = candidates;
    if (authoritative.occupied !== false) return { ok: false, reason: 'seat_unavailable' };

    const provisionalEligible = seat.dynamicPrice != null;
    const authoritativeEligible = authoritative.dynamicPrice != null;
    if (provisionalEligible !== authoritativeEligible) {
      return { ok: false, reason: 'eligibility_mismatch' };
    }

    const provisionalValue = Number(
      resolveBfoEffectiveSeatPrice({ pricing: provisionalPricing, seat }),
    );
    const authoritativeValue = Number(
      resolveBfoEffectiveSeatPrice({ pricing: authoritativePricing, seat: authoritative }),
    );
    if (provisionalValue !== authoritativeValue) return { ok: false, reason: 'value_mismatch' };

    matchedSeats.push(authoritative);
  }

  return { ok: true, seats: matchedSeats };
}

/**
 * Detects the typed BFO refresh-required prefix in a ticket state error string.
 * Only the typed prefix is inspected; localized text is never parsed.
 * @param {string} stateError - Ticket-poll state error entry.
 * @returns {boolean} True when the entry carries the typed BFO prefix.
 */
export function isBfoRefreshRequiredStateError(stateError) {
  return (
    stateError === BFO_PRICE_REFRESH_REQUIRED ||
    (typeof stateError === 'string' && stateError.startsWith(`${BFO_PRICE_REFRESH_REQUIRED}:`))
  );
}

/**
 * Detects the typed BFO refresh-required error preserved in a ticket-poll rejection.
 * @param {Object} reason - Poll rejection with `payload.errors.state`.
 * @returns {boolean} True when the first state error carries the typed BFO prefix.
 */
export function isBfoRefreshRequiredPollError(reason) {
  const state = reason?.payload?.errors?.state;
  return Array.isArray(state) && isBfoRefreshRequiredStateError(state[0]);
}

/**
 * Detects the typed BFO refresh-required code nested in a raw payment rejection,
 * before payment-engine-specific translation discards the original details.
 * @param {Object} error - Raw payment failure object.
 * @returns {boolean} True when the nested details code array carries the typed prefix.
 */
export function isBfoRefreshRequiredPaymentError(error) {
  const codes = error?.details?.code;
  return Array.isArray(codes) && codes.some(isBfoRefreshRequiredStateError);
}

/**
 * Recognizes the opaque initial ticket-request validation rejection shape with code
 * `100`. Callers must additionally require an active canonical BFO purchase in a
 * supported modern journey before treating it as BFO refresh-required.
 * @param {Object} reason - Initial ticket-request rejection with `errors` array.
 * @returns {boolean} True for the opaque code-100 initial lock rejection shape.
 */
export function isBfoOpaqueInitialLockError(reason) {
  const [firstError] = Array.isArray(reason?.errors) ? reason.errors : [];
  return firstError != null && Number(firstError.code) === 100;
}

/**
 * Checks the canonical BFO context for one applicable purchase leg.
 * @param {Object} purchase - Plain purchase state.
 * @param {string} way - Purchase leg (`departs`/`returns`, or UI aliases).
 * @returns {boolean} True only when this leg carries canonical BFO pricing.
 */
export function isBfoPurchaseLeg(purchase, way) {
  if (!purchase) return false;
  let purchaseWay = way;
  if (way === 'return') purchaseWay = 'returns';
  if (way === 'departure') purchaseWay = 'departs';
  const fragment = purchase[purchaseWay]?.fragments?.[0];
  const pricing = getApplicableLegPricing(fragment, {
    way: purchaseWay,
    isRoundTrip: Boolean(purchase.roundTrip),
  });
  const authoritativePricing = getApplicableLegPricing(purchase[purchaseWay]?.tripPricingFields, {
    way: purchaseWay,
    isRoundTrip: Boolean(purchase.roundTrip),
  });
  return isBfoPricing(pricing) || isBfoPricing(authoritativePricing);
}

/**
 * Checks canonical BFO context carried by the returned purchase leg itself. Cached
 * trip-details pricing is intentionally excluded so lock/payment errors cannot be
 * activated by stale client state.
 * @param {Object} purchase - Plain returned purchase state.
 * @param {string} way - Purchase leg (`departs`/`returns`, or UI aliases).
 * @returns {boolean} True only when the returned fragment is canonical BFO.
 */
export function isBfoReturnedPurchaseLeg(purchase, way) {
  if (!purchase) return false;
  let purchaseWay = way;
  if (way === 'return') purchaseWay = 'returns';
  if (way === 'departure') purchaseWay = 'departs';
  const fragment = purchase[purchaseWay]?.fragments?.[0];
  return isBfoPricing(
    getApplicableLegPricing(fragment, {
      way: purchaseWay,
      isRoundTrip: Boolean(purchase.roundTrip),
    }),
  );
}

/**
 * Checks whether any applicable purchase leg carries the canonical BFO context.
 * @param {Object} purchase - Plain purchase state.
 * @returns {boolean} True when at least one leg's applicable pricing is canonical BFO.
 */
export function isBfoPurchase(purchase) {
  if (!purchase) return false;

  return ['departs', 'returns'].some((way) => isBfoPurchaseLeg(purchase, way));
}

/**
 * Checks whether the returned purchase response itself has canonical BFO context.
 * @param {Object} purchase - Plain returned purchase state.
 * @returns {boolean} True when at least one returned purchase leg is canonical BFO.
 */
export function isBfoReturnedPurchase(purchase) {
  if (!purchase) return false;
  return ['departs', 'returns'].some((way) => isBfoReturnedPurchaseLeg(purchase, way));
}

/**
 * Lists canonical BFO purchase legs without allowing one leg to scope the other.
 * @param {Object} purchase - Plain purchase state.
 * @returns {Array<string>} Applicable purchase-way names.
 */
export function getBfoPurchaseWays(purchase) {
  return ['departs', 'returns'].filter((way) => isBfoPurchaseLeg(purchase, way));
}
